import { useState } from 'react';
import { Box, IconButton, IconButtonProps, ListItemIcon, Menu, MenuItem } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { MoreVert } from '@mui/icons-material';
import { ReactComponent as MoreIcon } from '../../assets/three-dots-vertical.svg';
import OutlinedButton from 'components/Button/OutlinedButton';
import { useTheme } from "@mui/material/styles";

type MenuItemType =
	| {
			key: string;
			label: string;
			icon: JSX.Element;
			hoverIcon?: JSX.Element;
			onMenuClick: (event?: any) => void;
			disabled?: boolean;
			isExportButton?: false;
			color?: string; 
			count?: number;
		}
	| {
			key: string;
			label: string;
			icon: JSX.Element;
			hoverIcon?: JSX.Element;
			onMenuClick: () => Promise<unknown>;
			disabled?: boolean;
			isExportButton: true;
			color?: string;
			count?: number;
		};

export type MoreOptionMenuProps = {
	menuItems: MenuItemType[];
	isDisabled?: boolean;
	isAdvance?: boolean;
	isProjectDetails?: boolean;
	onClick?: React.MouseEventHandler<HTMLElement> | undefined;
	onClose?: ((event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void) | undefined;
	iconColor?: string;
	keysWithBorder?: string[];
} & IconButtonProps;

function ExportButton({ onExport, children }: { onExport: () => Promise<unknown>; children: string }) {
	const [exporting, setExporting] = useState(false);
	return (
		<LoadingButton
			size="small"
			loading={exporting}
			loadingPosition="start"
			loadingIndicator="Exporting…"
			disabled={exporting}
			onClick={async () => {
				setExporting(true);
				await onExport();
				setExporting(false);
			}}
		>
			{children}
		</LoadingButton>
	);
}

const MoreOptionMenu: React.FC<MoreOptionMenuProps> = ({ menuItems, isAdvance, isProjectDetails, onClick, onClose, iconColor, keysWithBorder = [], ...props }) => {
	const theme = useTheme();
	const colors = theme.palette;
	const [moreEl, setMoreEl] = useState<null | HTMLElement>(null);

	let menuClassName = '';
	if (isProjectDetails) {
		menuClassName = 'project-details-menu custom-isolated-menu';
	} else if (menuItems?.some(item => item.key === 'allSavedQueries')) {
		menuClassName = 'search-allocate-menu';
	} else if (isAdvance) {
		menuClassName = 'custom-isolated-menu';
	} else if (menuItems?.some(item => item.key === 'copyToProgram')) {
		menuClassName = 'copy-program-menu';
	}

	const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
		setMoreEl(event.currentTarget);
		onClick?.(event);
	};
	const handleMoreClose = (event: any, reason: any) => {
		onClose?.(event, reason);
		setMoreEl(null);
	};

	return (
		<>
			{!isAdvance  ? 
				<IconButton
					aria-label="more"
					id="more-button"
					aria-controls={!!moreEl ? 'more-menu' : undefined}
					aria-expanded={!!moreEl ? 'true' : undefined}
					aria-haspopup="true"
					onClick={handleMoreClick}
					{...props}
					sx={{
						padding: '4px 2px',
						borderRadius: 0,
						width: '42px',
						height: '45px',
						color: 'white.main',
						...props.sx
					}}
				>
					<MoreVert sx={{ color: iconColor ?? 'white.main' }} />
				</IconButton>
				: <OutlinedButton
					sx={{ 
						color: 'info.main', 
						borderColor: 'info.main',
						width: '30px',
						height: '28px',
						ml: 1
					}}
					onClick={handleMoreClick}
				>
					<MoreIcon />
				</OutlinedButton>
			}
			<Menu
				id="more-menu"
				MenuListProps={{
					'aria-labelledby': 'more-button',
					autoFocusItem: false,
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right'
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right'
				}}
				anchorEl={moreEl}
				open={!!moreEl}
				onClose={handleMoreClose}
				className= {menuClassName}
				sx={{
					'& .theme-bg-blue .MuiMenu-paper, .theme-bg-blue .MuiPaper-root': {
						backgroundColor: 'none !important',
					},
					'& .MuiPaper-root': {
						overflow: 'hidden',
						minWidth: '240px',
						backgroundColor: '#003771 !important',
						borderRadius: '4px',
						boxShadow: '2px 5px 20px 0px #00000029',
						width: isAdvance ? '190px' : undefined,
						left: isAdvance ? '800px' : undefined,
					},
					'&.custom-isolated-menu .MuiPaper-root': {
						backgroundColor: '#003771 !important',
					},
					'&.search-allocate-menu .MuiPaper-root': {
						backgroundColor: `${colors.moreOptionDropdown.background} !important`,
					},
					'&.project-details-menu .MuiPaper-root': {
						backgroundColor: `${colors.moreOptionDropdown.background} !important`,
					},
					'& .MuiList-root': {
						padding: isAdvance ? '4px 16px' : '4px 0',
						height: 'auto'
					},
					'& .MuiMenuItem-root': {
						fontSize: '14px',
						fontWeight: 400,
						height: '35px',
						py: 3,
						pl: 1,
						pr: 1,
						'&:hover': {
							backgroundColor: '#002349',
							borderRadius: '4px',
							color: '#fff'
						},
						'&.search-allocate-menu .MuiMenuItem-root:hover': {
							backgroundColor: `${colors.moreOptionDropdown.menuItemHover} !important`,
						},
						'&:hover .icon-default': {
							display: 'none'
						},
						'&:hover .icon-hover': {
							display: 'block'
						},
						'& .text': {
							marginLeft: '5px'
						}
					},
					'&.project-details-menu .MuiMenuItem-root:hover': {
						backgroundColor: '#EFF6FF',
					},
					'&.copy-program-menu .MuiPaper-root': {
						backgroundColor: '#EFF6FF !important',
						color: '#444 !important',
					},
					'&.copy-program-menu .MuiPaper-root .MuiMenuItem-root:hover': {
						background: '#39ace733 !important',
						color: '#444 !important',
					},
				}}
				PaperProps={{
					style: {
						minWidth: '13ch'
					}
				}}
				slotProps={{
					paper: {
					sx: {
						backgroundColor: menuItems?.some(item => item.key === 'allSavedQueries') || isProjectDetails ? `${colors.moreOptionDropdown.background} !important` : '#003771 !important',
					},
					},
				}}
			>
				{menuItems.map(({ key, label, icon, hoverIcon, onMenuClick, disabled, isExportButton, color, count }) => {
					let labelColor: string;
					if (typeof color === 'string') {
						labelColor = color;
					} else if (isAdvance && key === 'viewHistory') {
						labelColor = '#009BD8';
					} else {
						labelColor = 'white.main';
					}

					let itemColor = '';
					if (key === 'unlink') {
						itemColor = isProjectDetails ? '#FE2626' : 'error.main';
					}

					let hoverLabelColor = '';
					if (isProjectDetails) {
						hoverLabelColor = key === 'unlink' ? '#FE2626' : '#009BD8';
					} else if (isAdvance) {
						hoverLabelColor = key === 'unlink' ? '#FF4C4C' : '#00BFFF';
					}

					let borderBottomColor = 'rgba(0, 0, 0, 0.12)';
					if (!isProjectDetails && isAdvance) {
						borderBottomColor = '#71758F';
					}

					return (
						<MenuItem
							key={key}
							disabled={disabled}
							sx={{
								color: itemColor,
								'&.Mui-disabled': {
									opacity: 0.7,
								},
								'&:hover .hover-label': {
									color: hoverLabelColor 
								},
								...(keysWithBorder.includes(key) ? {
								'&::after': {
									content: '""',
									position: 'absolute',
									bottom: 0,
									left: 0,
									right: 0,
									height: '1px',
									bgcolor: borderBottomColor,
								}
								} : {})
							}}
							onClick={(event) => {
								if (!isExportButton) {
									onMenuClick(event);
								}
								handleMoreClose(event, 'menuItem');
							}}
						>
							{isAdvance && <ListItemIcon sx={{ minWidth: 'unset'}}>
								<Box sx={{ position: 'relative', height: 22, width: 22 }}>
									<Box className="icon-default" sx={{ position: 'absolute', top: 0, left: 0 }}>
										{icon}
									</Box>
									<Box className="icon-hover" sx={{ display: 'none', position: 'absolute', top: 0, left: 0 }}>
										{hoverIcon}
									</Box>
								</Box>
							</ListItemIcon>}
							{!isAdvance && <ListItemIcon>{icon}</ListItemIcon>}
							{isExportButton ? (
								<ExportButton onExport={onMenuClick}>{label}</ExportButton>
							) : (
								<Box component="span" className= {isAdvance && ['link', 'saveAs', 'allSavedQueries', 'unlink', 'projectLink'].includes(key) ? "hover-label" : ''} color={labelColor}
									sx={{
										display: 'inline-flex',
										alignItems: 'center',
										gap: '6px'
									}}
								>
									{label}
									{key === 'allSavedQueries' && count !== undefined && (
										<Box
											component="span"
											sx={{
												backgroundColor: `${colors.moreOptionDropdown.chipBackground} !important`,
												color: `${colors.moreOptionDropdown.chipText} !important`,
												borderRadius: '999px',
												minWidth: '18px',
												height: '18px',
												px: count > 99 ? '6px' : count > 9 ? '5px' : '0px',
												fontSize: '12px',
												fontWeight: 500,
												display: 'inline-flex',
												alignItems: 'center',
												justifyContent: 'center',
												lineHeight: 1,
												flexShrink: 0
											}}
										>
											{count}
										</Box>
									)}
								</Box>
							)}
						</MenuItem>
					);
				})}
			</Menu>
		</>
	);
};

export default MoreOptionMenu;
